import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Stack,
  Text,
  Button,
  Box,
  Flex,
  useToast,
  SimpleGrid,
  Badge,
  Link,
  Tooltip,
} from '@chakra-ui/react';
import { SocialProfile } from 'src/api/v1-api';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { SocialProfileForm, SocialProfilesTable } from 'src/components/molecules';
import useRequest from 'src/utils/hooks/useRequest';
import UpsellTierModal from 'src/components/atoms/upsell-tier-modal';
import { FEATURE_IDS } from 'src/constants/tier-features';
import { getSocialProfileIcon } from 'src/utils/socialProfileIcons';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { CoverLoader } from 'src/components/atoms';

const AUTOPILOT_HELP_TEXT =
  'In order to enable Loma Autopilot features for any of your social accounts, we need you to directly connect your account(s). Establishing this initial connection won’t create any automation, but will allow you to enable different Autopilot features afterward. Contact Support with questions.';

const SocialProfiles = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [socialProfiles, setSocialProfiles] = useState<SocialProfile[]>([]);
  const [socialProfileSelected, setSocialProfileSelected] = useState<Partial<SocialProfile> | null>(
    null,
  );
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const socialProfileFormRef = useRef<HTMLDivElement>(null);
  const {
    marketplace,
    setMarketplace,
    connectedSocialProfiles,
    setConnectedSocialProfiles,
    setConnectedSocialProfilesFetched,
  } = useContext(MarketplaceContext);
  const { api } = useRequest();
  const toast = useToast();
  const [showAutoPilotToolTip, setShowAutoPilotToolTip] = useState<string | null>(null);

  useEffect(() => {
    if (marketplace?.social_profiles) {
      setSocialProfiles(marketplace.social_profiles);
    }
  }, [marketplace?.social_profiles]);

  useEffect(() => {
    if (marketplace) {
      getConnectedSocialProfiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace]);

  useEffect(() => {
    if (socialProfileSelected && socialProfileFormRef.current) {
      socialProfileFormRef.current.scrollIntoView();
    }
  }, [socialProfileSelected, socialProfileFormRef]);

  const getConnectedSocialProfiles = async () => {
    if (!marketplace) return;

    try {
      setIsLoading(true);
      const response = await api.shareautomation.connectedSocialProfiles.list(marketplace.id);
      setConnectedSocialProfiles(response.connected_accounts);
      setConnectedSocialProfilesFetched(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const createSocialProfile = async (socialProfile: Partial<SocialProfile>) => {
    if (!marketplace) return;
    try {
      setIsLoading(true);
      const response = await api.marketplace.socialProfiles.create(marketplace.id, socialProfile);
      const updatedSocialProfiles = [...socialProfiles, response];
      setSocialProfiles(updatedSocialProfiles);
      setMarketplace({
        ...marketplace,
        social_profiles: updatedSocialProfiles,
      });
      setSocialProfileSelected(null);
      toast({
        title: 'Social Profile Created',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error creating Social Profile. Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateSocialProfile = async (socialProfile: SocialProfile) => {
    if (!marketplace || !socialProfile?.id) return;

    try {
      setIsLoading(true);
      const response = await api.marketplace.socialProfiles.update(
        marketplace.id,
        socialProfile.id,
        socialProfile,
      );
      const updatedSocialProfiles = socialProfiles.map((sp) =>
        sp.id === socialProfile.id ? response : sp,
      );
      setSocialProfiles(updatedSocialProfiles);
      setMarketplace({
        ...marketplace,
        social_profiles: updatedSocialProfiles,
      });
      if (socialProfileSelected?.id === socialProfile.id) {
        setSocialProfileSelected(null);
      }
      toast({
        title: 'Social Profile Updated',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error updating Social Profile. Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSocialProfile = async (socialProfile: SocialProfile) => {
    if (!marketplace || !socialProfile?.id) return;

    try {
      setIsLoading(true);
      await api.marketplace.socialProfiles.delete(marketplace.id, socialProfile.id);
      const updatedSocialProfiles = socialProfiles.filter((sp) => sp.id !== socialProfile.id);
      setSocialProfiles(updatedSocialProfiles);
      setMarketplace({
        ...marketplace,
        social_profiles: updatedSocialProfiles,
      });
      if (socialProfileSelected?.id === socialProfile.id) {
        setSocialProfileSelected(null);
      }
      toast({
        title: 'Social Profile Deleted',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error deleting Social Profile. Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const newSocialProfile = () => {
    setSocialProfileSelected({});
  };

  const isSocialAutomationLocked =
    marketplace?.tier?.features[FEATURE_IDS.SOCIAL_AUTOMATION] === false;

  const managedAyrshareUrl = marketplace?.id
    ? `/api/marketplace/${marketplace.id}/manage_social_connected_accounts`
    : '#';

  const isNetworkConnected = (network: string) => {
    return connectedSocialProfiles?.some((p) => p.platform === network);
  };

  const autopilotNetworks: {
    label: string;
    value: 'facebook' | 'instagram' | 'twitter' | 'youtube';
  }[] = [
    { label: 'Facebook', value: 'facebook' },
    { label: 'Instagram', value: 'instagram' },
    { label: 'X / Twitter', value: 'twitter' },
    { label: 'YouTube', value: 'youtube' },
  ];

  return (
    <Stack position="relative">
      <Flex direction="column" gap={12}>
        <Flex direction="column">
          <Flex direction="row" alignItems="center" justifyContent="space-between" pb={4}>
            <Text fontWeight="semibold">Social Profiles</Text>
            <Button onClick={newSocialProfile}>Add New</Button>
          </Flex>
          <SocialProfilesTable
            socialProfiles={socialProfiles}
            editSocialProfile={setSocialProfileSelected}
            onUpdateSocialProfile={updateSocialProfile}
            onDeleteSocialProfile={deleteSocialProfile}
          />
        </Flex>
        <Flex direction="column" gap={4}>
          <Flex direction="row" alignItems="center" gap={2}>
            <Text fontWeight="semibold">Autopilot Connections</Text>
            <Tooltip
              hasArrow
              isOpen={showAutoPilotToolTip === `autopilot-connections`}
              label={AUTOPILOT_HELP_TEXT}
              bg="gray.100"
              color="black"
            >
              <InfoOutlineIcon
                onMouseEnter={() => setShowAutoPilotToolTip(`autopilot-connections`)}
                onMouseLeave={() => setShowAutoPilotToolTip(null)}
              />
            </Tooltip>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {autopilotNetworks.map((net) => {
              const connected = isNetworkConnected(net.value);
              return (
                <Box
                  key={net.value}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={connected ? 'green.200' : 'gray.200'}
                  p={4}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={3}
                >
                  <Flex alignItems="center" gap={2} width="100%" justifyContent="center">
                    {getSocialProfileIcon({ networkName: net.value })}
                    <Text fontWeight="medium">{net.label}</Text>
                    <Badge colorScheme={connected ? 'green' : 'gray'} size="sm">
                      {connected ? 'Linked' : 'Not linked'}
                    </Badge>
                  </Flex>
                  {isSocialAutomationLocked ? (
                    <Button size="sm" onClick={() => setIsUpsellModalOpen(true)}>
                      {connected ? 'Manage' : 'Connect'}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      as={Link}
                      href={managedAyrshareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {connected ? 'Manage' : 'Connect'}
                    </Button>
                  )}
                </Box>
              );
            })}
          </SimpleGrid>
        </Flex>
        <div
          ref={socialProfileFormRef}
          style={{ display: socialProfileSelected ? 'block' : 'none' }}
        >
          <SocialProfileForm
            socialProfile={socialProfileSelected}
            onCancel={() => setSocialProfileSelected(null)}
            onUpdateSocialProfile={updateSocialProfile}
            onCreateSocialProfile={createSocialProfile}
          />
        </div>
        {isLoading && <CoverLoader />}
        <UpsellTierModal
          isOpen={isUpsellModalOpen}
          onClose={() => setIsUpsellModalOpen(false)}
          featureId={FEATURE_IDS.SOCIAL_AUTOMATION}
        />
      </Flex>
    </Stack>
  );
};

export default SocialProfiles;
